import { POLL_MS } from '../lib/polling';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getDeviceInfo,
  getHardwareStatus,
  getNetworkStatus,
  isAndroidKiosk,
  type AndroidHardwareArea,
} from '../lib/androidBridge';
import { persistedDeviceId } from '../lib/deviceId';
import { usePosDataSource } from '../lib/posData/usePosData';
import { dateTime } from '../lib/format';
import { PageHeader } from '../components/Layout';
import { Empty, Loading, useToast } from '../components/ui';
import { useDeviceRegistration } from './DeviceSetup';

/**
 * Owner-facing troubleshooting screen (Phase 6 brief SS26-27): device
 * identity, native hardware status, and the sync engine's own diagnostics,
 * all read directly from the same sources the rest of the app already
 * trusts (androidBridge.ts, PosDataSource) - nothing here is a separate,
 * possibly-out-of-sync reporting path.
 *
 * Everything Android-specific quietly reports "not available" outside the
 * kiosk (isAndroidKiosk() false) rather than hiding the whole page, so an
 * owner checking from a browser still sees whatever the current data
 * source (sync engine) can tell them.
 */
export function DiagnosticsPage() {
  const kiosk = isAndroidKiosk();
  const device = kiosk ? getDeviceInfo() : null;
  const network = kiosk ? getNetworkStatus() : null;
  const hardware = kiosk ? getHardwareStatus() : null;
  const deviceId = kiosk ? persistedDeviceId() : undefined;
  const registration = useDeviceRegistration(deviceId);

  const { source } = usePosDataSource();
  const { push, fail } = useToast();
  const queryClient = useQueryClient();

  const sync = useQuery({
    queryKey: ['pos-sync-diagnostics'],
    queryFn: () => source!.getSyncDiagnostics(),
    enabled: Boolean(source),
    refetchInterval: POLL_MS,
  });

  const syncNow = useMutation({
    mutationFn: () => source!.syncNow(),
    onSuccess: () => {
      push('Sync requested');
      void queryClient.invalidateQueries({ queryKey: ['pos-sync-diagnostics'] });
      void queryClient.invalidateQueries({ queryKey: ['pos-pending-sync'] });
      void queryClient.invalidateQueries({ queryKey: ['pos-sync-status'] });
    },
    onError: fail,
  });

  return (
    <div className="stack">
      <PageHeader
        title="Device Diagnostics"
        subtitle="What this terminal, its hardware, and its sync engine actually report right now"
      />

      <div className="card">
        <div className="card-head">
          <h2>This device</h2>
        </div>
        <div className="card-body">
          {!kiosk ? (
            <Empty message="Not running on the Android kiosk - device and hardware diagnostics only apply there." />
          ) : (
            <div className="stack">
              <FieldGrid
                rows={[
                  ['Manufacturer / model', device ? `${device.manufacturer} ${device.model}` : '—'],
                  ['Android version', device?.androidVersion ?? '—'],
                  ['App version', device ? `${device.appVersion} (${device.appVersionCode})` : '—'],
                  ['Environment', device?.environment ?? '—'],
                  ['Kiosk (lock task) active', device ? (device.kioskActive ? 'Yes' : 'No') : '—'],
                  ['Network', network ? `${network.online ? 'Online' : 'Offline'} · ${network.transport}` : '—'],
                  [
                    'Registration',
                    registration.isLoading
                      ? 'Checking…'
                      : registration.data?.registered
                        ? `Registered as "${registration.data.name}"`
                        : 'Not registered at this property',
                  ],
                ]}
              />
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Hardware</h2>
        </div>
        <div className="card-body">
          {!kiosk || !hardware ? (
            <Empty message="Not running on the Android kiosk - nothing to report." />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Area</th>
                    <th>Status</th>
                    <th>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  <HardwareRow label="Receipt printer" area={hardware.printer} />
                  <HardwareRow label="Barcode scanner" area={hardware.barcode} />
                  <HardwareRow label="Cash drawer" area={hardware.cashDrawer} />
                  <HardwareRow label="Payment terminal" area={hardware.paymentTerminal} />
                  <HardwareRow label="Customer display" area={hardware.customerDisplay} />
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Sync engine</h2>
          {sync.data ? (
            <button className="btn sm" onClick={() => syncNow.mutate()} disabled={syncNow.isPending}>
              {syncNow.isPending ? 'Syncing…' : 'Sync now'}
            </button>
          ) : null}
        </div>
        <div className="card-body">
          {!source ? (
            <Loading label="Loading the data source…" />
          ) : sync.isLoading ? (
            <Loading />
          ) : !sync.data ? (
            <Empty message="This session talks to the server directly - there is no local outbox to report on." />
          ) : (
            <FieldGrid
              rows={[
                ['Status', sync.data.status],
                ['Network reachable', sync.data.networkUp ? 'Yes' : 'No'],
                ['Sync running now', sync.data.running ? 'Yes' : 'No'],
                ['Pending', String(sync.data.pending)],
                ['Sent, awaiting ack', String(sync.data.sent)],
                ['Failed', String(sync.data.failed)],
                ['Open conflicts', String(sync.data.openConflicts)],
                ['Consecutive failures', String(sync.data.consecutiveFailures)],
                ['Last push', sync.data.lastPushAt ? dateTime(sync.data.lastPushAt) : 'Never'],
                ['Last pull', sync.data.lastPullAt ? dateTime(sync.data.lastPullAt) : 'Never'],
                ['Last error', sync.data.lastError ?? '—'],
                ['Device code', sync.data.deviceCode],
                ['Protocol version', String(sync.data.protocolVersion)],
              ]}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function FieldGrid({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      {rows.map(([label, value]) => (
        <div className="row between" key={label}>
          <span className="small muted">{label}</span>
          <span className="small mono">{value}</span>
        </div>
      ))}
    </div>
  );
}

const HARDWARE_TONE: Record<AndroidHardwareArea['status'], string> = {
  SUPPORTED: 'ok',
  NOT_CONFIGURED: '',
  NOT_SUPPORTED: '',
};

function HardwareRow({ label, area }: { label: string; area: AndroidHardwareArea }) {
  return (
    <tr>
      <td>{label}</td>
      <td>
        <span className={`chip ${HARDWARE_TONE[area.status]}`}>{area.status.replace('_', ' ')}</span>
      </td>
      <td className="small muted">{area.detail}</td>
    </tr>
  );
}
